import { useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { css } from '@emotion/core';
import Button from '../../components/Button';
import EmailField from '../../components/EmailField';
import PageContainer from '../../components/PageContainer';
import PasswordField from '../../components/PasswordField';
import ResponseErrorTypes from '../../constants/ResponseErrorTypes';
import ErrorPageErrorTypes from '../../constants/ErrorPageErrorTypes';
import ResponseStatus from '../../constants/ResponseStatus';
import { isValidEmail } from '../../services/emailUtils';
import {
    getErrorMessage,
    getFirstError,
    showErrorNotificationFromResponse,
    navigateToErrorPage,
} from '../../services/errorUtils';
import UnauthenticatedRouteLanguageContext from '../../services/UnauthenticatedRouteLanguageContext';
import useMessageService from '../../services/i18n/useMessageService';
import useNetworkErrorNotification from '../../services/useNetworkErrorNotification';
import useNotificationCookie from '../../services/useNotificationCookie';
import useRequestState from '../../services/useRequestState';
import { updateUsername, updateUsernameWithVerificationCode } from '../../veevaIdApi';
import VerifyCodePage from '../verify-code-page';
import UsernameVerifiedConfirmation from './UsernameVerifiedConfirmation';

function ChangeUsernamePage() {
    const [
        [
            verifyLabel = 'Verify',
            passwordLabel = 'Password',
            completeVerificationTitle = 'Complete Verification',
            completeVerificationContent = 'To complete your email address update to {0}, login with your old email address.',
            currentEmailLabel = 'Current Email Address',
            invalidEmailError = 'Invalid email format entered',
        ],
    ] = useMessageService([
        'general.verify',
        'general.password',
        'username.email_complete_verification',
        'username.email_complete_verification_content',
        'username.email_current',
        'general.invalid_email',
    ]);
    const notificationCookie = useNotificationCookie();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const { showNotification } = useNetworkErrorNotification();
    const requestState = useRequestState();
    const [showVerifyCodePage, setShowVerifyCodePage] = useState(false);
    const history = useHistory();

    useEffect(() => {
        if (!notificationCookie) {
            navigateToErrorPage(history, { type: ErrorPageErrorTypes.GENERIC_ERROR });
        }
        // history is intentionally ignored because it rapidly changes and those changes do not affect navigation logic
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [notificationCookie]);

    const onEmailChange = (updatedEmail) => {
        setEmail(updatedEmail);
        setErrorMessage('');
    };
    const onPasswordChange = (updatedPassword) => {
        setPassword(updatedPassword);
        setErrorMessage('');
    };

    const { messageLanguage } = useContext(UnauthenticatedRouteLanguageContext);

    useEffect(() => {
        setErrorMessage('');
    }, [messageLanguage]);

    const handleVerify = async () => {
        if (!isValidEmail(email)) {
            setErrorMessage(invalidEmailError);
            return;
        }

        setErrorMessage('');
        setShowConfirmation(true);
        const response = await updateUsername(email, password, requestState);
        setShowConfirmation(false);
        switch (response.responseStatus) {
            case ResponseStatus.SUCCESS:
                if (response?.data?.verifyCode) {
                    setShowVerifyCodePage(true);
                } else if (response?.data?.targetUrl) {
                    window.location.href = response?.data?.targetUrl;
                }
                break;
            case ResponseStatus.FAILURE: {
                const responseError = getFirstError(response);
                switch (responseError.type) {
                    case ResponseErrorTypes.VEEVAID_INVALID_USERNAME_PASSWORD: {
                        const message = await getErrorMessage(responseError, messageLanguage);
                        setErrorMessage(message);
                        break;
                    }
                    default: {
                        const i18nOptions = { language: messageLanguage };
                        await showErrorNotificationFromResponse(response, i18nOptions);
                    }
                }
                break;
            }
            case ResponseStatus.UI_NETWORK_ERROR:
                await showNotification();
                break;
            default:
            // No-op
        }
    };

    const containerTitle = showConfirmation ? '' : completeVerificationTitle;

    const handleVerifyCodeSubmitStart = () => {
        setShowConfirmation(true);
    };

    const handleVerifyCodeSubmit = (code) => {
        return updateUsernameWithVerificationCode(code, requestState);
    };

    const handleSubmitSuccess = (response) => {
        window.location.href = response?.data?.targetUrl;
    };

    const handleVerifyCodeSubmitFail = () => {
        setShowConfirmation(false);
    };

    if (showVerifyCodePage) {
        return (
            <>
                {showConfirmation && (
                    <PageContainer title={containerTitle}>
                        <UsernameVerifiedConfirmation />
                    </PageContainer>
                )}
                <div
                    css={
                        showConfirmation &&
                        css`
                            display: none;
                        `
                    }
                >
                    <VerifyCodePage
                        onSubmitStart={handleVerifyCodeSubmitStart}
                        onSubmit={handleVerifyCodeSubmit}
                        onSubmitSuccess={handleSubmitSuccess}
                        onSubmitFail={handleVerifyCodeSubmitFail}
                    />
                </div>
            </>
        );
    }

    return (
        <PageContainer title={containerTitle}>
            {showConfirmation ? (
                <UsernameVerifiedConfirmation />
            ) : (
                <>
                    <p
                        css={css`
                            margin: 0;
                            padding-bottom: 1.667rem;
                        `}
                    >
                        {completeVerificationContent?.replace('{0}', notificationCookie?.email)}
                    </p>
                    <EmailField
                        error={!!errorMessage}
                        label={currentEmailLabel}
                        onChange={onEmailChange}
                        value={email}
                    />
                    <PasswordField
                        label={passwordLabel}
                        onChange={onPasswordChange}
                        value={password}
                        errorMessage={errorMessage}
                    />
                    <Button onClick={handleVerify} disabled={!email || !password}>
                        {verifyLabel}
                    </Button>
                </>
            )}
        </PageContainer>
    );
}

export default ChangeUsernamePage;
